import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Plus, Search, Filter, Download,
  Edit, Trash2, Eye, PlayCircle, PauseCircle,
  XCircle, RefreshCw, DollarSign, Calendar, Link2, Router, X, Loader
} from 'lucide-react';
import api from '../../services/api';
import DataTable from '../../components/Table/DataTable';
import { usePermissions } from '../../hooks/usePermissions';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import ExportButtons from '../../components/Export/ExportButtons';
import CancelarContratoModal from '../../components/Contracts/CancelarContratoModal';

const Contratos = () => {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [contratos, setContratos] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'todos'
  });
  const [showVinculoModal, setShowVinculoModal] = useState(false);
  const [contratoSelecionado, setContratoSelecionado] = useState(null);
  const [showCancelarModal, setShowCancelarModal] = useState(false);
  const [contratoParaCancelar, setContratoParaCancelar] = useState(null);

  const fetchContratos = async () => {
    try {
      setLoading(true);
      
      const contratosRes = await api.get(`/contratos?page=${pagination.page}&limit=${pagination.limit}`);
      
      const contratosData = contratosRes.data.data || contratosRes.data;
      
      setContratos(Array.isArray(contratosData) ? contratosData : []);
      setPagination({
        ...pagination,
        total: contratosRes.data.total || 0,
        totalPages: contratosRes.data.totalPages || 1
      });

      try {
        const statsRes = await api.get('/contratos/estatisticas');
        setEstatisticas(statsRes.data);
      } catch (statsError) {
        console.log('Estatísticas não disponíveis:', statsError);
      }
    } catch (error) {
      toast.error('Erro ao carregar contratos');
      console.error(error);
      setContratos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContratos();
  }, [pagination.page, pagination.limit]);

  const handleStatusChange = async (contrato, action) => {
    try {
      let endpoint = '';
      let successMessage = '';
      
      switch (action) {
        case 'suspender':
          endpoint = `/contratos/${contrato.id}/suspender`;
          successMessage = 'Contrato suspenso';
          break;
        case 'reativar':
          endpoint = `/contratos/${contrato.id}/reativar`;
          successMessage = 'Contrato reativado';
          break;
        case 'cancelar':
          setContratoParaCancelar(contrato);
          setShowCancelarModal(true);
          return;
        default:
          return;
      }
      
      await api.patch(endpoint);
      toast.success(successMessage);
      fetchContratos();
    } catch (error) {
      toast.error('Erro ao alterar status do contrato');
    }
  };

  const handleCancelarContrato = async (payload) => {
    if (!contratoParaCancelar) return;
    try {
      await api.post(`/contratos/${contratoParaCancelar.id}/cancelar`, payload);
      toast.success('Contrato cancelado');
      setShowCancelarModal(false);
      setContratoParaCancelar(null);
      fetchContratos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao cancelar contrato');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir este contrato?')) {
      try {
        await api.delete(`/contratos/${id}`);
        toast.success('Contrato excluído com sucesso');
        fetchContratos();
      } catch (error) {
        toast.error('Erro ao excluir contrato');
      }
    }
  };

  const handleOpenVinculoModal = (contrato) => {
    setContratoSelecionado(contrato);
    setShowVinculoModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'ativo': 'bg-green-100 text-green-800',
      'suspenso': 'bg-yellow-100 text-yellow-800',
      'cancelado': 'bg-red-100 text-red-800',
      'aguardando': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const columns = [
    {
      header: 'Ações',
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(`/contratos/${row.original.id}`)}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Ver detalhes"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate(`/assinacontrato/${row.original.id}`)}
            className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-900"
            title="Assinar contrato"
          >
            <Calendar className="w-4 h-4 text-green-600" />
          </button>
          
          {canEdit('contratos') && (
            <>
              <button
                onClick={() => handleOpenVinculoModal(row.original)}
                className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900"
                title="Vínculos rápidos"
              >
                <Link2 className="w-4 h-4 text-blue-600" />
              </button>

              <button
                onClick={() => navigate(`/contratos/${row.original.id}/editar`)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Editar"
              >
                <Edit className="w-4 h-4" />
              </button>
              
              {row.original.status === 'ativo' && (
                <button
                  onClick={() => handleStatusChange(row.original, 'suspender')}
                  className="p-1 rounded hover:bg-yellow-100 dark:hover:bg-yellow-900"
                  title="Suspender"
                >
                  <PauseCircle className="w-4 h-4 text-yellow-600" />
                </button>
              )}
              
              {row.original.status === 'suspenso' && (
                <button
                  onClick={() => handleStatusChange(row.original, 'reativar')}
                  className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-900"
                  title="Reativar"
                >
                  <PlayCircle className="w-4 h-4 text-green-600" />
                </button>
              )}
              
              {row.original.status !== 'cancelado' && (
                <button
                  onClick={() => handleStatusChange(row.original, 'cancelar')}
                  className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900"
                  title="Cancelar"
                >
                  <XCircle className="w-4 h-4 text-red-600" />
                </button>
              )}
            </>
          )}
          
          {canDelete('contratos') && (
            <button
              onClick={() => handleDelete(row.original.id)}
              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          )}
        </div>
      )
    },
    {
      header: 'Cliente',
      accessorKey: 'cliente',
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">
            {row.original.cliente?.nome || 'N/A'}
          </p>
          <p className="text-xs text-gray-500">
            {row.original.cliente?.cpfCnpj || '-'}
          </p>
        </div>
      )
    },
    {
      header: 'Plano',
      accessorKey: 'plano',
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.plano?.nomePlano || 'N/A'}</p>
          <p className="text-xs text-gray-500">
            {row.original.plano?.limitDownload || 0}MB / {row.original.plano?.limitUpload || 0}MB
          </p>
        </div>
      )
    },
    {
      header: 'Valor',
      accessorKey: 'valorMensalidade',
      cell: ({ getValue, row }) => {
        const valorAtual = parseFloat(getValue() || 0);
        const valorOriginal = parseFloat(row.original.valorOriginal || 0);
        
        return (
          <div>
            <p className="font-medium">R$ {valorAtual.toFixed(2)}</p>
            {valorAtual !== valorOriginal && valorOriginal > 0 && (
              <p className="text-xs text-gray-500 line-through">
                R$ {valorOriginal.toFixed(2)}
              </p>
            )}
          </div>
        );

        
      }
    },
    {
      header: 'Vencimento',
      accessorKey: 'diaVencimento',
      cell: ({ row }) => (
        <div>
          <p className="text-sm">Dia {row.original.diaVencimento}</p>
          <p className="text-xs text-gray-500">
            {row.original.formaPagamento}
          </p>
        </div>
      )
    },
    {
      header: 'Período',
      cell: ({ row }) => {
        try {
          const dataInicio = row.original.dataInicio 
            ? format(new Date(row.original.dataInicio), 'dd/MM/yyyy') 
            : '-';
          const dataFim = row.original.dataFim 
            ? format(new Date(row.original.dataFim), 'dd/MM/yyyy') 
            : null;
          
          return (
            <div className="text-sm">
              <p>{dataInicio}</p>
              {dataFim && (
                <p className="text-xs text-gray-500">até {dataFim}</p>
              )}
            </div>
          );
        } catch (error) {
          return <div className="text-sm text-gray-500">Data inválida</div>;
        }
      }
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(row.original.status)}`}>
          {row.original.status}
        </span>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Contratos
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gerencie os contratos dos clientes
          </p>
        </div>
        
        {canCreate('contratos') && (
          <button
            onClick={() => navigate('/contratos/novo')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Novo Contrato
          </button>
        )}
      </div>

      {/* Estatísticas */}
      {estatisticas && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-600 dark:text-gray-400">Ativos</p>
            <p className="text-2xl font-bold text-green-600">{estatisticas.ativos}</p>
          </div>
          <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-600 dark:text-gray-400">Suspensos</p>
            <p className="text-2xl font-bold text-yellow-600">{estatisticas.suspensos}</p>
          </div>
          <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-600 dark:text-gray-400">Cancelados</p>
            <p className="text-2xl font-bold text-red-600">{estatisticas.cancelados}</p>
          </div>
          <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-600 dark:text-gray-400">Aguardando</p>
            <p className="text-2xl font-bold text-blue-600">{estatisticas.aguardando}</p>
          </div>
          <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
            <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{estatisticas.total}</p>
          </div>
          <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-600 dark:text-gray-400">Receita Mensal</p>
            <p className="text-xl font-bold text-primary-600">
              R$ {estatisticas.receitaMensal?.toLocaleString('pt-BR')}
            </p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por cliente, plano..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800"
              />
            </div>
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-800"
          >
            <option value="todos">Todos os Status</option>
            <option value="ativo">Ativos</option>
            <option value="suspenso">Suspensos</option>
            <option value="cancelado">Cancelados</option>
            <option value="aguardando">Aguardando</option>
          </select>
          
          <ExportButtons 
            data={contratos}
            filename="contratos"
            title="Relatório de Contratos"
          />
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
        <DataTable
          columns={columns}
          data={contratos}
          loading={loading}
          pagination={pagination}
          onPageChange={(page) => setPagination({ ...pagination, page })}
          onLimitChange={(limit) => setPagination({ ...pagination, limit, page: 1 })}
        />
      </div>

      {/* Modal de Vínculos Rápidos */}
      {showVinculoModal && (
        <VinculoRapidoModal
          contrato={contratoSelecionado}
          onClose={() => {
            setShowVinculoModal(false);
            setContratoSelecionado(null);
          }}
          onSuccess={() => {
            fetchContratos();
          }}
        />
      )}
      {/* Modal de Cancelamento */}
      {showCancelarModal && (
        <CancelarContratoModal
          open={showCancelarModal}
          onClose={() => { setShowCancelarModal(false); setContratoParaCancelar(null); }}
          onSubmit={handleCancelarContrato}
        />
      )}
    </div>
  );
};

// Modal de Vínculo Rápido
const VinculoRapidoModal = ({ contrato, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('equipamentos');
  const [loading, setLoading] = useState(false);
  const [contratoAtual, setContratoAtual] = useState(contrato);
  
  // Estados para Equipamentos
  const [equipamentosDisponiveis, setEquipamentosDisponiveis] = useState([]);
  const [equipamentosVinculados, setEquipamentosVinculados] = useState([]);
  const [loadingEquipamentos, setLoadingEquipamentos] = useState(true);
  const [formEquipamento, setFormEquipamento] = useState({
    equipamentoId: '',
    tipoVinculo: 'comodato',
    dataInstalacao: format(new Date(), 'yyyy-MM-dd'),
    observacoes: ''
  });
  
  // Estados para Serviços
  const [servicosDisponiveis, setServicosDisponiveis] = useState([]);
  const [servicosVinculados, setServicosVinculados] = useState([]);
  const [loadingServicos, setLoadingServicos] = useState(true);
  const [servicoSelecionado, setServicoSelecionado] = useState('');

  useEffect(() => {
    if (activeTab === 'equipamentos') {
      fetchEquipamentos();
    } else {
      fetchServicos();
    }
  }, [activeTab]);

  // Buscar detalhes atualizados do contrato para garantir que valores exibidos estejam corretos
  const fetchContratoDetalhes = async () => {
    try {
      if (!contrato?.id) return;
      const res = await api.get(`/contratos/${contrato.id}`);
      setContratoAtual(res.data);
    } catch (error) {
      console.error('Erro ao carregar detalhes do contrato:', error);
    }
  };

  useEffect(() => {
    // ao abrir o modal, garante que temos os dados mais recentes
    fetchContratoDetalhes();
  }, [contrato?.id]);

  const fetchEquipamentos = async () => {
    try {
      setLoadingEquipamentos(true);
      const [disponiveis, vinculados] = await Promise.all([
        api.get('/equipamentos/disponiveis'),
        api.get(`/contratos/${contrato.id}/equipamentos`)
      ]);
      setEquipamentosDisponiveis(disponiveis.data);
      setEquipamentosVinculados(Array.isArray(vinculados.data) ? vinculados.data : []);
    } catch (error) {
      console.error('Erro ao carregar equipamentos:', error);
      toast.error('Erro ao carregar equipamentos');
    } finally {
      setLoadingEquipamentos(false);
    }
  };

  const fetchServicos = async () => {
    try {
      setLoadingServicos(true);
      const [disponiveis, vinculados] = await Promise.all([
        api.get('/servicos-adicionais/ativos'),
        api.get(`/contratos/${contrato.id}/servicos-adicionais`)
      ]);
      
      const vinculadosArray = Array.isArray(vinculados.data) ? vinculados.data : [];
      const disponiveisArray = Array.isArray(disponiveis.data) ? disponiveis.data : [];
      
      // Filtrar serviços que já estão vinculados
      const servicosNaoVinculados = disponiveisArray.filter(
        s => !vinculadosArray.find(sv => sv.id === s.id)
      );
      
      setServicosDisponiveis(servicosNaoVinculados);
      setServicosVinculados(vinculadosArray);
    } catch (error) {
      console.error('Erro ao carregar serviços:', error);
      toast.error('Erro ao carregar serviços adicionais');
    } finally {
      setLoadingServicos(false);
    }
  };

  const handleVincularEquipamento = async (e) => {
    e.preventDefault();
    if (!formEquipamento.equipamentoId) {
      toast.error('Selecione um equipamento');
      return;
    }
    
    try {
      setLoading(true);
      await api.post(`/contratos/${contrato.id}/equipamentos`, {
        equipamentoId: Number(formEquipamento.equipamentoId),
        tipoVinculo: formEquipamento.tipoVinculo,
        dataInstalacao: formEquipamento.dataInstalacao || null,
        observacoes: formEquipamento.observacoes || ''
      });
      
      toast.success('Equipamento vinculado com sucesso!');
      setFormEquipamento({
        equipamentoId: '',
        tipoVinculo: 'comodato',
        dataInstalacao: format(new Date(), 'yyyy-MM-dd'),
        observacoes: ''
      });
      await fetchEquipamentos();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao vincular equipamento');
    } finally {
      setLoading(false);
    }
  };

  const handleDesvincularEquipamento = async (vinculoId) => {
    if (!window.confirm('Deseja realmente desvincular este equipamento?')) return;
    
    try {
      setLoading(true);
      await api.delete(`/contratos/equipamentos/vinculos`, {
        data: {
          vinculoId: Number(vinculoId),
          motivo: 'Desvinculado via acesso rápido'
        }
      });
      
      toast.success('Equipamento desvinculado com sucesso!');
      await fetchEquipamentos();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao desvincular equipamento');
    } finally {
      setLoading(false);
    }
  };

  const handleAdicionarServico = async (e) => {
    e.preventDefault();
    if (!servicoSelecionado) {
      toast.error('Selecione um serviço');
      return;
    }
    
    try {
      setLoading(true);
      await api.post(`/contratos/${contrato.id}/servicos-adicionais`, {
        servicoAdicionalId: Number(servicoSelecionado)
      });
      
      // Recalcular valor
      const servicosRes = await api.get(`/contratos/${contrato.id}/servicos-adicionais`);
      const servicosVinculados = servicosRes.data;
      const valorServicos = servicosVinculados.reduce((total, s) => total + parseFloat(s.valorMensal || 0), 0);
      const valorPlano = parseFloat(contrato?.plano?.valor || 0);
      const novoValorMensalidade = valorPlano + valorServicos;
      
  toast.success('Serviço adicional vinculado com sucesso!');
  setServicoSelecionado('');
  await fetchServicos();
  await fetchContratoDetalhes();
  onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao adicionar serviço');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoverServico = async (servicoId) => {
    if (!window.confirm('Deseja realmente remover este serviço?')) return;
    
    try {
      setLoading(true);
      await api.delete(`/contratos/${contrato.id}/servicos-adicionais`, {
        data: {
          servicoAdicionalId: Number(servicoId),
          motivo: 'Removido via acesso rápido'
        }
      });
      
      const servicosRes = await api.get(`/contratos/${contrato.id}/servicos-adicionais`);
      const servicosVinculados = servicosRes.data;
      const valorServicos = servicosVinculados.reduce((total, s) => total + parseFloat(s.valorMensal || 0), 0);
      const valorPlano = parseFloat(contrato?.plano?.valor || 0);
      const novoValorMensalidade = valorPlano + valorServicos;
      
  toast.success('Serviço removido com sucesso!');
  await fetchServicos();
  await fetchContratoDetalhes();
  onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover serviço');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-card rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Link2 className="w-6 h-6 text-blue-600" />
                Vínculos Rápidos
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Contrato #{contrato.id} - {contrato.cliente?.nome}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex">
            <button
              onClick={() => setActiveTab('equipamentos')}
              className={`flex-1 px-6 py-3 font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'equipamentos'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Router className="w-4 h-4" />
              Equipamentos ({equipamentosVinculados.length})
            </button>
            <button
              onClick={() => setActiveTab('servicos')}
              className={`flex-1 px-6 py-3 font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'servicos'
                  ? 'border-b-2 border-purple-600 text-purple-600 dark:text-purple-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Package className="w-4 h-4" />
              Serviços Adicionais ({servicosVinculados.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'equipamentos' ? (
            <div className="space-y-6">
              {/* Form Vincular Equipamento */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Vincular Novo Equipamento
                </h3>
                {loadingEquipamentos ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <form onSubmit={handleVincularEquipamento} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Equipamento *</label>
                        <select
                          value={formEquipamento.equipamentoId}
                          onChange={(e) => setFormEquipamento({ ...formEquipamento, equipamentoId: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 text-sm"
                          required
                        >
                          <option value="">Selecione...</option>
                          {equipamentosDisponiveis.map((equip) => (
                            <option key={equip.id} value={equip.id}>
                              {equip.modelo} - {equip.macAddress}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Tipo de Vínculo *</label>
                        <select
                          value={formEquipamento.tipoVinculo}
                          onChange={(e) => setFormEquipamento({ ...formEquipamento, tipoVinculo: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 text-sm"
                        >
                          <option value="comodato">Comodato</option>
                          <option value="venda">Venda</option>
                          <option value="aluguel">Aluguel</option>
                          <option value="cortesia">Cortesia</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Data de Instalação</label>
                        <input
                          type="date"
                          value={formEquipamento.dataInstalacao}
                          onChange={(e) => setFormEquipamento({ ...formEquipamento, dataInstalacao: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Observações</label>
                        <input
                          type="text"
                          value={formEquipamento.observacoes}
                          onChange={(e) => setFormEquipamento({ ...formEquipamento, observacoes: e.target.value })}
                          placeholder="Informações adicionais..."
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 text-sm"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loading || equipamentosDisponiveis.length === 0}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      {loading ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Vinculando...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          Vincular Equipamento
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Lista de Equipamentos Vinculados */}
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Equipamentos Vinculados ({equipamentosVinculados.length})
                </h3>
                {loadingEquipamentos ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : equipamentosVinculados.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {equipamentosVinculados.map((equip) => (
                      <div 
                        key={equip.id} 
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <Router className="w-5 h-5 text-blue-500" />
                            <p className="font-medium text-sm">{equip.equipamento?.modelo || 'N/A'}</p>
                          </div>
                          <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded">
                            {equip.status || 'ativo'}
                          </span>
                        </div>
                        <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400 mb-3">
                          <p><span className="font-medium">MAC:</span> {equip.equipamento?.macAddress || '-'}</p>
                          <p><span className="font-medium">Tipo:</span> {equip.tipoVinculo || '-'}</p>
                          {equip.dataInstalacao && (
                            <p><span className="font-medium">Instalado em:</span> {format(new Date(equip.dataInstalacao), 'dd/MM/yyyy')}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleDesvincularEquipamento(equip.id)}
                          disabled={loading}
                          className="w-full px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-xs font-medium transition-colors disabled:opacity-50"
                        >
                          Desvincular
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                    <Router className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">Nenhum equipamento vinculado</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Form Adicionar Serviço */}
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Adicionar Serviço Adicional
                </h3>
                {loadingServicos ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-6 h-6 animate-spin text-purple-600" />
                  </div>
                ) : (
                  <form onSubmit={handleAdicionarServico} className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Serviço *</label>
                      <select
                        value={servicoSelecionado}
                        onChange={(e) => setServicoSelecionado(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 text-sm"
                        required
                      >
                        <option value="">Selecione um serviço...</option>
                        {servicosDisponiveis.map((servico) => (
                          <option key={servico.id} value={servico.id}>
                            {servico.nome} - R$ {parseFloat(servico.valorMensal).toFixed(2)}/mês
                            {servico.categoria ? ` (${servico.categoria})` : ''}
                          </option>
                        ))}
                      </select>
                      {servicosDisponiveis.length === 0 && (
                        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                          ⚠️ Todos os serviços disponíveis já estão vinculados
                        </p>
                      )}
                    </div>

                    {/* Preview do Serviço */}
                    {servicoSelecionado && servicosDisponiveis.find(s => s.id === Number(servicoSelecionado)) && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
                        {(() => {
                          const servico = servicosDisponiveis.find(s => s.id === Number(servicoSelecionado));
                          return (
                            <>
                              {servico.descricao && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                  {servico.descricao}
                                </p>
                              )}
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-gray-500">Valor Mensal:</span>
                                  <p className="font-bold text-purple-600">
                                    R$ {parseFloat(servico.valorMensal).toFixed(2)}
                                  </p>
                                </div>
                                {servico.valorInstalacao > 0 && (
                                  <div>
                                    <span className="text-gray-500">Instalação:</span>
                                    <p className="font-semibold">
                                      R$ {parseFloat(servico.valorInstalacao).toFixed(2)}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading || servicosDisponiveis.length === 0}
                      className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      {loading ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Adicionando...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          Adicionar Serviço
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Lista de Serviços Vinculados */}
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Serviços Vinculados ({servicosVinculados.length})
                </h3>
                {loadingServicos ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : servicosVinculados.length > 0 ? (
                  <>
                    {/* Resumo Financeiro */}
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800 mb-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Valor Total dos Serviços</p>
                        </div>
                        <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                          + R$ {servicosVinculados.reduce((total, s) => total + parseFloat(s.valorMensal || 0), 0).toFixed(2)}
                          <span className="text-xs font-normal text-gray-500">/mês</span>
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {servicosVinculados.map((servico) => (
                        <div 
                          key={servico.id} 
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:shadow-md transition-shadow"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2 flex-1">
                              <Package className="w-5 h-5 text-purple-500" />
                              <div>
                                <p className="font-medium text-sm">{servico.nome}</p>
                                {servico.categoria && (
                                  <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded">
                                    {servico.categoria}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {servico.descricao && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                              {servico.descricao}
                            </p>
                          )}
                          <div className="mb-3">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-gray-500">Valor Mensal:</span>
                              <span className="font-bold text-purple-600">
                                R$ {parseFloat(servico.valorMensal || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoverServico(servico.id)}
                            disabled={loading}
                            className="w-full px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-xs font-medium transition-colors disabled:opacity-50"
                          >
                            Remover
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Info sobre valor total */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mt-4">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        <strong>Valor Total do Contrato:</strong> Plano (R$ {parseFloat(contratoAtual?.valorOriginal || contrato.valorOriginal || 0).toFixed(2)}) + 
                        Serviços (R$ {servicosVinculados.reduce((total, s) => total + parseFloat(s.valorMensal || 0), 0).toFixed(2)}) = 
                        <strong className="ml-1">R$ {parseFloat(contratoAtual?.valorMensalidade || contrato.valorMensalidade || 0).toFixed(2)}/mês</strong>
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">Nenhum serviço adicional vinculado</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-medium"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default Contratos;